import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IAgencyDigest } from './agencyDigest';
import { RECENT_WEEKS } from './agencyThresholds';
import { BRAND, CHART_INK, STANDING_COLOR } from './agencyChartTheme';

const useStyles = createUseStyles({
    trend: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 18
    },

    half: {
        flex: 1
    },

    label: {
        fontSize: 12.5,
        color: CHART_INK.label
    },

    value: {
        fontSize: 28,
        fontWeight: 600,
        lineHeight: 1.1,
        color: BRAND.deepTeal,
        fontVariantNumeric: 'tabular-nums'
    },

    unit: {
        fontSize: 12.5,
        color: CHART_INK.label,
        marginLeft: 5
    },

    verdict: {
        marginTop: 12,
        fontSize: 13,
        lineHeight: 1.5,
        color: CHART_INK.label
    },

    verdictMark: {
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: '50%',
        marginRight: 7
    }
});

/**
 * Whether the caseload's attention load is rising or falling, as one sentence and two figures.
 *
 * Deliberately not a claim about outcomes. Nothing here says the service prevented an ER visit,
 * because nothing in the record can support that: the app sees findings and what is on file, not
 * counterfactuals. The comparison is the earlier part of the observed window against the recent
 * part, and it says so on the surface. It is not a comparison against life before Innerhive, and
 * presenting it as one would be inventing a baseline nobody measured.
 */
export const AttentionTrend: FC<{ digest: IAgencyDigest }> = ({ digest }) => {
    const classes = useStyles();

    const { attentionTrend, windowWeeks } = digest;
    const earlierWeeks = windowWeeks - RECENT_WEEKS;
    const change = Math.round((attentionTrend.recentPerWeek - attentionTrend.earlierPerWeek) * 10) / 10;

    const direction = change > 0 ? 'above' : change < 0 ? 'settled' : 'steady';
    const verdict = change > 0
        ? `The caseload is raising ${Math.abs(change)} more findings a week than it was earlier in the window.`
        : change < 0
            ? `The caseload is raising ${Math.abs(change)} fewer findings a week than it was earlier in the window.`
            : 'The caseload is raising findings at the same rate as earlier in the window.';

    return (
        <div data-id="attention-trend">
            <div className={classes.trend}>
                <div className={classes.half}>
                    <div className={classes.label}>First {earlierWeeks} weeks</div>
                    <div className={classes.value}>
                        {attentionTrend.earlierPerWeek}
                        <span className={classes.unit}>a week</span>
                    </div>
                </div>
                <div className={classes.half}>
                    <div className={classes.label}>Last {RECENT_WEEKS} weeks</div>
                    <div className={classes.value}>
                        {attentionTrend.recentPerWeek}
                        <span className={classes.unit}>a week</span>
                    </div>
                </div>
            </div>

            <div className={classes.verdict} data-id="attention-trend-verdict">
                <span className={classes.verdictMark} style={{ background: STANDING_COLOR[direction] }} />
                {verdict} A rise is not automatically bad: more findings can mean the record is being
                worked rather than that clients are declining.
            </div>
        </div>
    );
};
